import { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { fetchAcademySetup } from "app/actions/academySetup";
import {
  AcademySetupItem,
  withCompletion,
} from "app/components/AcademySetup/items";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import useActions from "./useActions";

/**
 * The academy setup checklist items merged with API-derived completion from
 * the academySetup store slice. Each eligible mount (coach on an academy
 * plan) refetches so completion stays current; concurrent mounts dedupe
 * inside the thunk. `loaded` stays false until the first response, so
 * consumers can hide instead of flashing an all-incomplete list; `failed`
 * says that response was an error, so a page can stop waiting instead of
 * spinning until the next mount retries.
 */
export default function useAcademySetupItems(): {
  items: AcademySetupItem[];
  loaded: boolean;
  failed: boolean;
  /** No optimistic optional mark is still awaiting its PUT. */
  settled: boolean;
} {
  const self = useSelector(
    (state: RootState) => state.user.self,
    shallowEqual,
  );
  const ownerId = useSelector((state: RootState) => state.academySetup.ownerId);
  const storedCompletion = useSelector(
    (state: RootState) => state.academySetup.data,
    shallowEqual,
  );
  const storedOptionalStates = useSelector(
    (state: RootState) => state.academySetup.optionalStates,
    shallowEqual,
  );
  // A slice cached for another account (the store survives sign-out) reads
  // as not loaded until this account's response lands.
  const owned = ownerId === (self ? self.id : null);
  const completion = owned ? storedCompletion : null;
  const optionalStates = owned ? storedOptionalStates : {};
  const pendingOptional = useSelector(
    (state: RootState) => state.academySetup.pendingOptional,
    shallowEqual,
  );
  const error = useSelector((state: RootState) => state.academySetup.error);
  const actions = useActions({ fetchAcademySetup });
  const eligible = isAcademyOnboardingCoach(self);

  // Keyed by the account as well: a change of user while a surface stays
  // mounted leaves `eligible` true, so the id is what triggers the refetch.
  useEffect(() => {
    if (eligible) {
      actions.fetchAcademySetup();
    }
  }, [eligible, self?.id, actions]);

  return {
    items: withCompletion(completion, optionalStates),
    loaded: completion !== null,
    // Only this account's request state counts: the previous account's
    // failed fetch says nothing about the one now signed in.
    failed: owned && completion === null && error !== null,
    settled: owned && Object.keys(pendingOptional).length === 0,
  };
}
